import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BordBadge, BordOneToneIcon, BordPhoto } from 'bord-design-system';
import QuoteDetailBanner from '../QuoteDetailBanner/QuoteDetailBanner';
import { bannerVariant, IDeviceSuggestedItem } from 'types/quoteDetails.types';
import { EQuoteItemStatusCode, IQuoteItem, IQuoteItemStatus } from 'types/quote.types';
import { formatQuotePrice } from 'views/Quote/utils/formatQuotePrice';
import './QuoteDeviceApprovalCard.scss';

interface IQuoteDeviceApprovalCard {
  device: IQuoteItem;
  quoteId?: number;
}

const QUOTING_STATUSES = [EQuoteItemStatusCode.PENDING_QUOTE, EQuoteItemStatusCode.QUOTING];

/**
 * Card de dispositivo cotizado con información de producto, sugerencias, precios y estado de aprobación del cliente.
 */
const QuoteDeviceApprovalCard = ({ device, quoteId }: IQuoteDeviceApprovalCard) => {
  const { push } = useHistory();
  const cardRef = useRef<HTMLDivElement>(null);

  const [productNameMaxWidth, setProductNameMaxWidth] = useState(0);

  const getStatusInfo = (quoteStatus?: IQuoteItemStatus): { label: string; color: string } => {
    if (!quoteStatus?.code) return { label: '', color: '' };
    const labelMap: Record<EQuoteItemStatusCode, string> = {
      [EQuoteItemStatusCode.PENDING_QUOTE]: 'Por cotizar',
      [EQuoteItemStatusCode.QUOTING]: 'Por cotizar',
      [EQuoteItemStatusCode.QUOTED]: 'Cotizado',
      [EQuoteItemStatusCode.PENDING_APPROVAL]: 'Por aprobar',
      [EQuoteItemStatusCode.APPROVED]: 'Aprobado',
      [EQuoteItemStatusCode.PURCHASED]: 'Aprobado',
      [EQuoteItemStatusCode.REJECTED]: 'Rechazado',
      [EQuoteItemStatusCode.UNAVAILABLE]: 'Rechazado',
      [EQuoteItemStatusCode.EXPIRED]: 'Por cotizar',
      [EQuoteItemStatusCode.CANCELLED]: 'Cancelada'
    };
    const colorMap: Record<EQuoteItemStatusCode, string> = {
      [EQuoteItemStatusCode.PENDING_QUOTE]: '#E29E28',
      [EQuoteItemStatusCode.QUOTING]: '#E29E28',
      [EQuoteItemStatusCode.QUOTED]: '#00994F',
      [EQuoteItemStatusCode.PENDING_APPROVAL]: '#4096F0',
      [EQuoteItemStatusCode.APPROVED]: '#00994F',
      [EQuoteItemStatusCode.PURCHASED]: '#00994F',
      [EQuoteItemStatusCode.REJECTED]: '#DE072E',
      [EQuoteItemStatusCode.UNAVAILABLE]: '#DE072E',
      [EQuoteItemStatusCode.EXPIRED]: '#E29E28',
      [EQuoteItemStatusCode.CANCELLED]: '#DE072E'
    };
    return { label: labelMap[quoteStatus.code], color: colorMap[quoteStatus.code] };
  };

  const suggestedItems = useMemo(() => {
    const suggestedItems = [...(device?.suggestedItems ?? [])];
    if (
      !device?.isGenericProduct &&
      device?.quoteStatus?.code &&
      !QUOTING_STATUSES.includes(device?.quoteStatus?.code)
    ) {
      suggestedItems?.unshift({
        ...device,
        id: device.quoteItemId,
        productName: undefined,
        sku: undefined,
        quoteStatus: undefined
      });
    }
    return suggestedItems;
  }, [device]);

  const statusInfo = getStatusInfo(device?.quoteStatus);
  const isPurchased = device?.quoteStatus?.code === EQuoteItemStatusCode.PURCHASED;
  const isRejected =
    device?.quoteStatus?.code === EQuoteItemStatusCode.REJECTED ||
    device?.quoteStatus?.code === EQuoteItemStatusCode.UNAVAILABLE;
  const isCancelled = device?.quoteStatus?.code === EQuoteItemStatusCode.CANCELLED;
  const isQuoting = QUOTING_STATUSES.includes(device?.quoteStatus?.code as EQuoteItemStatusCode);
  const hasBottomData = !!suggestedItems?.length;
  const showBrandImage = !device?.isGenericProduct && !!device?.brandImage;

  /** Construye el rango de entrega, evitando mostrar "null - null" cuando las fechas vienen de tienda y no están asignadas. */
  const getDeliveryEstimate = (suggestedItem: IDeviceSuggestedItem) => {
    const { etaMin, etaMax, etaPeriod } = suggestedItem ?? {};
    const periodLabel = etaPeriod === 'days' ? 'días' : 'semanas';
    if (etaMin == null || etaMax == null) return 'Segun tienda';
    return `${etaMin} - ${etaMax} ${periodLabel}`;
  };
  const procurementReason =
    device?.procurementReason || suggestedItems?.find(item => item?.procurementReason)?.procurementReason;

  const getSuggestedItemCols = (suggestedItem: IDeviceSuggestedItem) => [
    { label: 'PROVEEDOR', value: suggestedItem?.providerName, highlight: false },
    {
      label: 'STOCK DISPONIBLE',
      value: suggestedItem?.quantity !== undefined ? String(suggestedItem.quantity) : undefined,
      highlight: false
    },
    {
      label: 'COSTO',
      value: formatQuotePrice(
        (Number(suggestedItem?.providerUnitPrice) || 0) * (Number(suggestedItem?.exchangeRate) || 0),
        suggestedItem?.currency
      ),
      highlight: false
    },
    {
      label: 'PRECIO DE VENTA',
      value: formatQuotePrice(suggestedItem?.totalPrice, suggestedItem?.saleCurrency ?? 'USD'),
      highlight: true
    },
    {
      label: 'ESTIMADO DE ENTREGA',
      value: getDeliveryEstimate(suggestedItem),
      highlight: false
    }
  ];

  const updateNameMaxWidth = () => {
    const cardWidth = cardRef.current?.clientWidth ?? 0;
    setProductNameMaxWidth((cardWidth - 514) / 10);
  };
  const validationBordPhoto = () => {
    if (!device?.imageUrl)
      return (
        <div className="productImagePlaceholder">
          <BordOneToneIcon variant="laptopPhoneSync" standardSize={32} stroke="#B4B4B4" />
        </div>
      );
    return (
      <>
        <BordPhoto
          size="s56"
          url={device?.imageUrl || ''}
          style={{ border: '0.1rem solid #B4B4B4', borderRadius: '1.2rem' }}
        />
        {showBrandImage && (
          <div className="brandContainer">
            <BordPhoto
              variant="circle"
              size="s32"
              url={device?.brandImage || ''}
              style={{ border: '0.1rem solid #B4B4B4' }}
            />
          </div>
        )}
      </>
    );
  };

  const handleDeviceSkuClick = (productId?: number | null, categoryId?: number | null) => {
    if (!productId || !categoryId) return;
    window.open(`/catalog/create-product/${categoryId}?productId=${productId}`, '_blank');
  };

  useEffect(() => {
    const observer = new ResizeObserver(updateNameMaxWidth);
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`quoteDeviceApprovalCard${isRejected ? ' rejected' : ''}${isCancelled ? ' cancelled' : ''}`}
    >
      <div
        className="cardTop"
        onClick={() => !isCancelled && push(`/quote/edit/${quoteId}`, { quoteItemId: device?.quoteItemId })}
      >
        <div className="productLeft">
          <div className="productImageWrapper">{validationBordPhoto()}</div>
          <div className="productInfo">
            <div className="productName" style={{ maxWidth: `${productNameMaxWidth}rem` }}>
              {device?.productName}
            </div>
            {device?.isGenericProduct ? (
              <BordBadge label="Personalizado" className="customBadge" variant="gradient" size="small" />
            ) : (
              <div className="productTags">
                {Object.entries(device?.pills ?? {})
                  .filter(([label]) => !label.includes('Cargo') && !label.includes('Perfil') && !label.includes('Gama'))
                  .map(([label, value]) => (
                    <div key={label} className="specTag">
                      {value}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className="productMeta">
          <div className="metaCol">
            <div className="metaLabel">Categoría</div>
            <div className="metaValue">{device?.categoryName}</div>
          </div>
          <div className="metaCol">
            <div className="metaLabel">Unidades</div>
            <div className="metaValueRow">
              <span className="metaValue">{device?.quantity}</span>
              {device?.wasModified && <span className="modifiedBadge">Editado</span>}
            </div>
          </div>
          {!isPurchased &&
            (isQuoting || !device?.isGenericProduct ? (
              <div className="metaCol">
                <div className="metaLabel">Estado</div>
                <div className="metaStatus">
                  <div className="statusDot" style={{ backgroundColor: statusInfo.color }} />
                  <div className="metaValue">{statusInfo.label}</div>
                </div>
              </div>
            ) : (
              <div className="metaCol" />
            ))}
        </div>
      </div>
      {hasBottomData && !isCancelled && (
        <div className="cardBottomContainer">
          {procurementReason && (
            <div className="cardSuggestion">
              <QuoteDetailBanner
                variant={bannerVariant.information}
                content={
                  <div>
                    <span style={{ color: '#4096F0' }}>Razón de las sugerencias:</span> {procurementReason}
                  </div>
                }
              />
            </div>
          )}
          {suggestedItems?.map(suggestedItem => {
            const isSuggestedItemRejected = suggestedItem?.quoteStatus?.code === EQuoteItemStatusCode.REJECTED;
            const suggestedItemStatusInfo = getStatusInfo(suggestedItem?.quoteStatus);
            return (
              <div
                key={suggestedItem.id}
                className={`cardBottom${isSuggestedItemRejected ? ' rejectedSuggestedItem' : ''}`}
              >
                {(suggestedItem?.productName || suggestedItem?.sku) && (
                  <div className="suggestedItemHeader">
                    <div className="suggestedItemHeaderLeft">
                      {suggestedItem?.productName && (
                        <div className="suggestedItemProductName">{suggestedItem.productName}</div>
                      )}
                      {suggestedItem?.sku && (
                        <div className="suggestedItemSku">
                          {suggestedItem.sku}
                          <div
                            className="skuIcon"
                            onClick={() => handleDeviceSkuClick(suggestedItem.productId, suggestedItem.categoryId)}
                          >
                            <BordOneToneIcon variant="externalLink" standardSize={12} stroke="#00B188" />
                          </div>
                        </div>
                      )}
                    </div>
                    {suggestedItem?.quoteStatus &&
                      suggestedItem?.productName &&
                      suggestedItem?.sku &&
                      suggestedItem?.quoteStatus?.code !== EQuoteItemStatusCode.PURCHASED && (
                        <div className="suggestedItemStatus">
                          <div className="statusDot" style={{ backgroundColor: suggestedItemStatusInfo.color }} />
                          <div className="statusLabel">{suggestedItemStatusInfo.label}</div>
                        </div>
                      )}
                  </div>
                )}
                {(suggestedItem?.productName || suggestedItem?.sku) && <div className="suggestedItemDivider" />}
                {getSuggestedItemCols(suggestedItem)?.map(col => (
                  <div key={col.label} className="bottomCol">
                    <div className="bottomLabel">{col.label}</div>
                    <div className={`bottomValue${col.highlight ? ' highlight' : ''}`}>{col.value}</div>
                  </div>
                ))}
                {isSuggestedItemRejected && (
                  <div className="suggestedItemRejectedBanner">
                    <BordOneToneIcon variant="xCircle" standardSize={16} stroke="#EA3323" />
                    <div className="suggestedItemRejectedReason">
                      <div className="reasonLabel">Razón:</div>
                      <div className="reasonText">{suggestedItem?.customerReason}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {isRejected && (
        <div className="cardRejectedBanner">
          <div className="rejectedReason">
            <BordOneToneIcon variant="xCircle" standardSize={14} stroke="#DE072E" />
            <div className="reasonLabel">Razón:</div>
            <div className="reasonText">{device?.customerReason}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDeviceApprovalCard;
